import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { DiagnosticFormData } from '../types/diagnostic';
import SecurityScore from '../components/results/SecurityScore';
import { ComparisonCard } from '../components/results/ComparisonCard';
import { useAuth } from '../contexts/AuthContext';
import { OPTISECUR_COMPANY_ID } from '../config/constants';
import { calculateSecurityScore } from '../utils/securityCalculator';
import { generateRecommendedActions } from '../services/recommendationService';
import { createDiagnostic } from '../services/diagnosticService';
import { bulkCreateActionsFromRecommendations } from '../services/progressTrackingService';
import toast from 'react-hot-toast';
import { dpsTheme } from '../styles/dpsTheme';
import { savePendingDiagnostic } from '../utils/pendingDiagnosticStorage';
import { useDeviceSize } from '../hooks/useMediaQuery';

export default function Results() {
  const { isMobile } = useDeviceSize();
  const location = useLocation();
  const navigate = useNavigate();
  const topRef = useRef<HTMLDivElement>(null);
  const { currentUser, userProfile } = useAuth();
  const formData = location.state?.formData as DiagnosticFormData;
  const [savedDiagnosticId, setSavedDiagnosticId] = useState<string | null>(
    location.state?.diagnosticId || null
  );
  const [isSaving, setIsSaving] = useState(false);
  const [hasScrolledToTop, setHasScrolledToTop] = useState(false);
  const hasSavedRef = useRef(false);

  // Rôles staff : accès direct au rapport, pas de paiement Stripe
  const STAFF_ROLES = ['advisor', 'company_admin', 'super_admin'];
  const hasDirectAccess = !!(
    currentUser &&
    userProfile && (
      STAFF_ROLES.includes(userProfile.role) ||
      userProfile.company_id !== OPTISECUR_COMPANY_ID
    )
  );

  useEffect(() => {
    if (!formData) {
      navigate('/diagnostic');
      return;
    }

    if (!hasScrolledToTop) {
      topRef.current?.scrollIntoView({ behavior: 'smooth' });
      setHasScrolledToTop(true);
    }

    const saveDiagnostic = async () => {
  // Attendre que userProfile soit chargé pour connaître le rôle exact
  if (currentUser && userProfile && !savedDiagnosticId && !hasSavedRef.current) {
    try {
      hasSavedRef.current = true;
      setIsSaving(true);
      const scoreData = calculateSecurityScore(formData);
      const recommendedActions = generateRecommendedActions(formData, scoreData.categoryScores);

      const isStaffRole = STAFF_ROLES.includes(userProfile.role);

      // UNIQUEMENT CET APPEL - il fait tout (diagnostic + snapshot + actions)
      const diagnostic = await createDiagnostic(
        currentUser.id,
        formData,
        scoreData.score,
        scoreData.categoryScores.protection,
        scoreData.categoryScores.surveillance,
        scoreData.categoryScores.deterrence,
        recommendedActions,
        userProfile.company_id ?? OPTISECUR_COMPANY_ID,
        formData.clientReference ?? null,
        hasDirectAccess,   // report_paid = true pour staff et clients white-label
        isStaffRole ? currentUser.id : null  // advisor_id = soi-même si staff
      );

      if (diagnostic?.id) {
        // Créer les actions d'amélioration
        await bulkCreateActionsFromRecommendations(
          diagnostic.id,
          recommendedActions.map(action => ({
            id: action.id,
            title: action.title,
            category: action.category as 'protection' | 'surveillance' | 'dissuasion',
            priority: action.priority as 'high' | 'medium' | 'low',
            estimatedCost: action.estimatedCost,
            scoreImpact: action.scoreImpact,
            description: action.description
          }))
        );

        setSavedDiagnosticId(diagnostic.id);
        // Remplacer l'entrée d'historique avec le diagnosticId pour que tout
        // retour arrière (navigation back) sur cette page initialise
        // savedDiagnosticId et empêche un second createDiagnostic.
        navigate('/results', {
          state: { formData, diagnosticId: diagnostic.id },
          replace: true,
        });
        toast.success('Diagnostic sauvegardé dans votre compte');
      }
    } catch (error) {
      console.error('Error saving diagnostic:', error);
    } finally {
      setIsSaving(false);
    }
  }
};

    saveDiagnostic();
  }, [formData, currentUser, userProfile, navigate, savedDiagnosticId]);

  const handleAccessPremium = () => {
    if (!currentUser) {
      const saved = savePendingDiagnostic(formData);
      if (!saved) {
        toast.error('Erreur lors de la sauvegarde temporaire. Veuillez réessayer.');
        return;
      }
      toast('Créez un compte gratuit ou connectez-vous pour accéder au rapport premium', {
        icon: '🔒',
        duration: 4000
      });
      navigate('/login', { state: { from: '/results', formData } });
      return;
    }
    navigate('/checkout', { state: { formData, diagnosticId: savedDiagnosticId } });
  };

  if (!formData) {
    return null;
  }

  const { score } = calculateSecurityScore(formData);
  const hasCrimeData = formData.crimeData && formData.crimeComparison;
  const isHighRisk = hasCrimeData && formData.crimeComparison!.isAboveAverage && formData.crimeComparison!.relativeDifference > 20;

  return (
    <div ref={topRef} style={{
      fontFamily: dpsTheme.typography.fontFamily,
      minHeight: '100vh',
      background: dpsTheme.colors.background.primary,
      paddingTop: '64px',
      color: dpsTheme.colors.text.secondary
    }}>
      <div style={{ maxWidth: '1400px', margin: '0 auto', padding: isMobile ? dpsTheme.spacing.lg : dpsTheme.spacing['3xl'] }}>
        <div style={{ marginBottom: dpsTheme.spacing['2xl'] }}>
          <h1 style={{
            fontSize: isMobile ? dpsTheme.typography.fontSize.xl : dpsTheme.typography.fontSize['4xl'],
            fontWeight: dpsTheme.typography.fontWeight.bold,
            color: dpsTheme.colors.text.primary,
            marginBottom: '8px',
            lineHeight: dpsTheme.typography.lineHeight.tight
          }}>Résultats de votre Diagnostic de Performance Sécuritaire</h1>
          <p style={{
            fontSize: isMobile ? dpsTheme.typography.fontSize.sm : dpsTheme.typography.fontSize.lg,
            color: dpsTheme.colors.text.muted
          }}>Voici un aperçu gratuit de votre score de sécurité</p>
        </div>

        {/* Score global */}
        <div style={{ marginBottom: dpsTheme.spacing['2xl'] }}>
          <SecurityScore formData={formData} showLimited={true} />
        </div>

        {/* NOUVEAU : Comparaison anonyme */}
        <div style={{ marginBottom: dpsTheme.spacing['2xl'] }}>
          <ComparisonCard 
            userScore={score} 
            housingType={formData.housingType}
            showDetailedMessage={true}
          />
        </div>

        {/* Cambriolages dans la commune — carte simplifiée */}
        {hasCrimeData && (() => {
          const cd = formData.crimeData!;
          const rate = cd.tauxPour1000Logements;
          const nationalRate = 3.95;
          const isAbove = rate > nationalRate;
          const isHigh = rate > 6;
          const accentColor = isHigh ? '#ef4444' : isAbove ? '#f97316' : '#10b981';
          const communeName = cd.commune || formData.city || 'votre commune';

          let contextMsg: string;
          if (isHigh) {
            contextMsg = `Votre commune présente un risque élevé, nettement au-dessus de la moyenne nationale (${nationalRate.toFixed(2)}‰). Votre sécurité mérite une attention urgente.`;
          } else if (isAbove) {
            contextMsg = `Votre commune dépasse la moyenne nationale (${nationalRate.toFixed(2)}‰). Un plan d'action personnalisé vous aidera à réduire votre exposition.`;
          } else {
            contextMsg = `Votre commune est en dessous de la moyenne nationale (${nationalRate.toFixed(2)}‰). Gardez une bonne vigilance pour maintenir ce niveau.`;
          }

          return (
            <div style={{
              background: `${accentColor}08`,
              border: `1px solid ${accentColor}30`,
              borderRadius: dpsTheme.borderRadius.lg,
              padding: isMobile ? '20px 16px' : '24px 28px',
              marginBottom: dpsTheme.spacing['2xl'],
            }}>
              {/* En-tête */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '20px' }}>
                <div style={{
                  width: '36px', height: '36px', borderRadius: '50%', flexShrink: 0,
                  background: `${accentColor}20`, border: `1px solid ${accentColor}40`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center', color: accentColor
                }}>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
                    <circle cx="12" cy="10" r="3" />
                  </svg>
                </div>
                <div>
                  <div style={{ fontSize: dpsTheme.typography.fontSize.base, fontWeight: dpsTheme.typography.fontWeight.semibold, color: dpsTheme.colors.text.primary }}>
                    Cambriolages à {communeName}
                  </div>
                  <div style={{ fontSize: dpsTheme.typography.fontSize.xs, color: dpsTheme.colors.text.muted }}>
                    Source : Ministère de l'Intérieur • données {cd.annee}
                  </div>
                </div>
              </div>

              {/* Deux chiffres clés */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '16px' }}>
                <div style={{
                  background: 'rgba(255,255,255,0.04)', border: `1px solid ${dpsTheme.colors.border.primary}`,
                  borderRadius: dpsTheme.borderRadius.md, padding: '16px', textAlign: 'center'
                }}>
                  <div style={{ fontSize: isMobile ? '2rem' : '2.5rem', fontWeight: dpsTheme.typography.fontWeight.bold, color: accentColor, lineHeight: 1 }}>
                    {cd.cambriolages}
                  </div>
                  <div style={{ fontSize: dpsTheme.typography.fontSize.xs, color: dpsTheme.colors.text.muted, marginTop: '6px' }}>
                    cambriolages en {cd.annee}
                  </div>
                </div>
                <div style={{
                  background: 'rgba(255,255,255,0.04)', border: `1px solid ${dpsTheme.colors.border.primary}`,
                  borderRadius: dpsTheme.borderRadius.md, padding: '16px', textAlign: 'center'
                }}>
                  <div style={{ fontSize: isMobile ? '2rem' : '2.5rem', fontWeight: dpsTheme.typography.fontWeight.bold, color: accentColor, lineHeight: 1 }}>
                    {rate.toFixed(2)}<span style={{ fontSize: '1.2rem' }}>‰</span>
                  </div>
                  <div style={{ fontSize: dpsTheme.typography.fontSize.xs, color: dpsTheme.colors.text.muted, marginTop: '6px' }}>
                    pour 1 000 logements
                  </div>
                </div>
              </div>

              {/* Message contextuel */}
              <p style={{
                fontSize: dpsTheme.typography.fontSize.sm, color: dpsTheme.colors.text.secondary,
                lineHeight: dpsTheme.typography.lineHeight.relaxed, margin: 0
              }}>
                {contextMsg}
              </p>
            </div>
          );
        })()}

        {hasDirectAccess ? (
          /* ── CTA accès direct (staff + clients white-label) ── */
          <div style={{
            background: 'linear-gradient(135deg, rgba(16,185,129,0.12), rgba(16,185,129,0.05))',
            backdropFilter: 'blur(20px)',
            border: '1px solid rgba(16,185,129,0.3)',
            borderRadius: dpsTheme.borderRadius.xl,
            padding: isMobile ? '24px 16px' : '40px',
            marginBottom: dpsTheme.spacing['2xl'],
            textAlign: 'center' as const
          }}>
            <button
              onClick={() => savedDiagnosticId && navigate(`/dps/report/${savedDiagnosticId}`)}
              disabled={!savedDiagnosticId || isSaving}
              style={{
                padding: isMobile ? '12px 20px' : '14px 32px',
                borderRadius: '8px',
                fontWeight: 600,
                fontSize: isMobile ? '14px' : '16px',
                cursor: (!savedDiagnosticId || isSaving) ? 'not-allowed' : 'pointer',
                transition: 'all 0.2s',
                border: 'none',
                background: (!savedDiagnosticId || isSaving) ? 'rgba(16,185,129,0.3)' : 'linear-gradient(135deg, #10b981, #059669)',
                color: 'white',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '8px',
                opacity: (!savedDiagnosticId || isSaving) ? 0.7 : 1
              }}
            >
              {isSaving ? (
                <>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="animate-spin">
                    <path d="M21 12a9 9 0 1 1-6.219-8.56" />
                  </svg>
                  Préparation du rapport...
                </>
              ) : (
                <>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                    <polyline points="14 2 14 8 20 8" />
                  </svg>
                  Accéder au rapport complet
                </>
              )}
            </button>
          </div>
        ) : (
          /* ── CTA Stripe (clients OptiSecur direct) ── */
          <div style={{
            background: `linear-gradient(135deg, ${dpsTheme.colors.accent.blue.bg}, rgba(37, 99, 235, 0.1))`,
            backdropFilter: 'blur(20px)',
            border: `1px solid ${dpsTheme.colors.accent.blue.border}`,
            borderRadius: dpsTheme.borderRadius.xl,
            padding: isMobile ? '24px 16px' : '40px',
            marginBottom: dpsTheme.spacing['2xl']
          }}>
          <div style={{ textAlign: 'center' }}>
            {isHighRisk ? (
              <svg width={isMobile ? 48 : 64} height={isMobile ? 48 : 64} viewBox="0 0 24 24" fill="none" stroke="#ef4444" strokeWidth="2" style={{ margin: '0 auto 16px' }}>
                <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
                <line x1="12" y1="9" x2="12" y2="13" />
                <line x1="12" y1="17" x2="12.01" y2="17" />
              </svg>
            ) : (
              <svg width={isMobile ? 48 : 64} height={isMobile ? 48 : 64} viewBox="0 0 24 24" fill="none" stroke={dpsTheme.colors.accent.blue.primary} strokeWidth="2" style={{ margin: '0 auto 16px' }}>
                <rect x="5" y="11" width="14" height="10" rx="2" ry="2" />
                <circle cx="12" cy="16" r="1" />
                <path d="M8 11V7a4 4 0 0 1 8 0v4" />
              </svg>
            )}
            <h2 style={{
              fontSize: isMobile ? dpsTheme.typography.fontSize.lg : dpsTheme.typography.fontSize['2xl'],
              fontWeight: dpsTheme.typography.fontWeight.bold,
              color: dpsTheme.colors.text.primary,
              marginBottom: '12px'
            }}>
              {isHighRisk ? "Sécurisez votre logement dès maintenant" : "Accédez au rapport DPS complet"}
            </h2>
            <p style={{
              color: dpsTheme.colors.text.secondary,
              marginBottom: '24px',
              maxWidth: '700px',
              margin: '0 auto 24px',
              lineHeight: dpsTheme.typography.lineHeight.relaxed
            }}>
              {hasCrimeData && isHighRisk ? (
                <>
                  Votre commune enregistre <strong>{formData.crimeData!.cambriolages} cambriolages</strong> par an.
                  Découvrez comment mieux protéger votre logement avec notre rapport DPS complet :
                  scores détaillés, cartographie des vulnérabilités, recommandations personnalisées et simulateur d'améliorations.
                </>
              ) : (
                <>
                  Débloquez l'analyse détaillée de votre sécurité : scores par catégorie, cartographie des vulnérabilités,
                  recommandations personnalisées, simulateur d'améliorations, et bien plus encore.
                </>
              )}
            </p>

            <div style={{
              display: 'grid',
              gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fit, minmax(200px, 1fr))',
              gap: '16px',
              marginBottom: '24px',
              maxWidth: '900px',
              margin: '0 auto 24px'
            }}>
              <div style={{
                background: dpsTheme.colors.background.card,
                backdropFilter: 'blur(20px)',
                border: `1px solid ${dpsTheme.colors.border.primary}`,
                borderRadius: dpsTheme.borderRadius.md,
                padding: '16px'
              }}>
                <div style={{
                  fontWeight: dpsTheme.typography.fontWeight.semibold,
                  color: dpsTheme.colors.text.primary,
                  marginBottom: '4px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px'
                }}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={dpsTheme.colors.status.completed.text} strokeWidth="2">
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                  Analyse détaillée
                </div>
                <div style={{ fontSize: dpsTheme.typography.fontSize.sm, color: dpsTheme.colors.text.muted }}>
                  3 scores par catégorie + graphiques
                </div>
              </div>
              <div style={{
                background: dpsTheme.colors.background.card,
                backdropFilter: 'blur(20px)',
                border: `1px solid ${dpsTheme.colors.border.primary}`,
                borderRadius: dpsTheme.borderRadius.md,
                padding: '16px'
              }}>
                <div style={{
                  fontWeight: dpsTheme.typography.fontWeight.semibold,
                  color: dpsTheme.colors.text.primary,
                  marginBottom: '4px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px'
                }}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={dpsTheme.colors.status.completed.text} strokeWidth="2">
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                  Recommandations
                </div>
                <div style={{ fontSize: dpsTheme.typography.fontSize.sm, color: dpsTheme.colors.text.muted }}>
                  Conseils priorisés et chiffrés
                </div>
              </div>
              <div style={{
                background: dpsTheme.colors.background.card,
                backdropFilter: 'blur(20px)',
                border: `1px solid ${dpsTheme.colors.border.primary}`,
                borderRadius: dpsTheme.borderRadius.md,
                padding: '16px'
              }}>
                <div style={{
                  fontWeight: dpsTheme.typography.fontWeight.semibold,
                  color: dpsTheme.colors.text.primary,
                  marginBottom: '4px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px'
                }}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={dpsTheme.colors.status.completed.text} strokeWidth="2">
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                  Simulateur
                </div>
                <div style={{ fontSize: dpsTheme.typography.fontSize.sm, color: dpsTheme.colors.text.muted }}>
                  Projetez vos améliorations
                </div>
              </div>
            </div>

            <button
              onClick={handleAccessPremium}
              style={{
                padding: isMobile ? '12px 16px' : '16px 32px',
                borderRadius: '8px',
                fontWeight: 600,
                fontSize: isMobile ? '14px' : '18px',
                cursor: 'pointer',
                transition: 'all 0.2s',
                border: 'none',
                background: dpsTheme.colors.accent.blue.gradient,
                color: 'white',
                boxShadow: dpsTheme.shadows.button,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
                width: isMobile ? '100%' : 'auto'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 8px 20px rgba(59, 130, 246, 0.5)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = dpsTheme.shadows.button;
              }}
            >
              Générer mon rapport complet DPS — 9,90 €
            </button>
            <p style={{
              fontSize: dpsTheme.typography.fontSize.sm,
              color: dpsTheme.colors.text.muted,
              marginTop: '12px'
            }}>Paiement sécurisé • Accès immédiat</p>
          </div>
          </div>
        )}

      </div>
    </div>
  );
}